import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate} from 'react-router-dom';
import { Toaster} from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionsProvider} from './hooks/usePermissions';
import Layout from './components/Layout';
import Login from './components/Login';
import ServerStatus from './components/ServerStatus';
import Dashboard from './components/Dashboard';
import Members from './components/Members';
import Vendors from './components/Vendors';
import Inventory from './components/Inventory';
import JobCards from './components/JobCards';
import AllJobCards from './components/AllJobCards';
import ViewJobCardPage from './components/jobcards/ViewJobCardPage';
import Schedule from './components/Schedule';
import KnowledgeBase from './components/KnowledgeBase';
import CustomerManagement from './components/CustomerManagement';
import RegisterVehicle from './components/RegisterVehicle';
import VehicleMaster from './components/VehicleMaster';
import Accounting from './components/Accounting';
import Settings from './components/Settings';
import Reports from './components/Reports';
import Profile from './components/Profile';
import AdminRoute from './components/AdminRoute';
import ProtectedRoute from './components/ProtectedRoute';
import DefaultRoute from './components/DefaultRoute';
import SaleReturn from './components/SaleReturn';
import ServiceReminder from './components/ServiceReminder';
import VendorPayable from './components/VendorPayable';
import Branches from './components/Branches';
import ExpenseManagement from './components/ExpenseManagement';
import OwnerTransactions from './components/OwnerTransactions';
import ErrorBoundary from './components/ErrorBoundary';

import QuotationForm from './components/accounting/QuotationForm';
import QuotationView from './components/accounting/QuotationView';
import InvoiceForm from './components/accounting/InvoiceForm';
import InvoiceView from './components/accounting/InvoiceView';
import BatchInvoicePrint from './components/accounting/BatchInvoicePrint';
import PaymentForm from './components/accounting/PaymentForm';
import InvoiceList from './components/accounting/InvoiceList';
import CrmDashboard from './components/crm/CrmDashboard';
import LeadsList from './components/crm/LeadsList';
import LeadForm from './components/crm/LeadForm';
import LeadDetails from './components/crm/LeadDetails';
import ActivitiesList from './components/crm/ActivitiesList';
import DealsPipeline from './components/crm/DealsPipeline';
import DealForm from './components/crm/DealForm';
import DealDetails from './components/crm/DealDetails';
import TaskManager from './components/crm/TaskManager';
import TaskForm from './components/crm/TaskForm';
import Analytics from './components/crm/Analytics';
import './index.css';

function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <ServerStatus />
      <Toaster position="top-right" />
      <Routes>
        <Route path="/login" element={<Login />} />
        {!user ? (
          <Route path="*" element={<Navigate to="/login" replace />} />
        ) : (
        <Route path="/" element={<Layout />}>
          <Route index element={<DefaultRoute />} />
          <Route path="dashboard" element={<ProtectedRoute requiredPermissions={['dashboard.read']}><Dashboard /></ProtectedRoute>} />
          <Route path="members" element={<ProtectedRoute requiredPermissions={['members.read']}><Members /></ProtectedRoute>} />
          <Route path="vendors" element={<ProtectedRoute requiredPermissions={['vendors.read']}><Vendors /></ProtectedRoute>} />
          <Route path="inventory" element={<ProtectedRoute requiredPermissions={['inventory.read']}><Inventory /></ProtectedRoute>} />
          <Route path="register-vehicle" element={<ProtectedRoute requiredPermissions={['vehicles.read']}><RegisterVehicle /></ProtectedRoute>} />
          <Route path="vehicle-master" element={<ProtectedRoute requiredPermissions={['vehicles.read']}><VehicleMaster /></ProtectedRoute>} />

            <Route path="accounts" element={<ProtectedRoute requiredPermissions={['accounts.read', 'invoices.read', 'quotations.read', 'payments.read']}><Accounting /></ProtectedRoute>} />
            <Route path="accounts/quotations/create" element={<ProtectedRoute requiredPermissions={['quotations.create']}><QuotationForm /></ProtectedRoute>} />
            <Route path="accounts/quotations/:id" element={<ProtectedRoute requiredPermissions={['quotations.read']}><QuotationView /></ProtectedRoute>} />
            <Route path="accounts/quotations/:id/edit" element={<ProtectedRoute requiredPermissions={['quotations.update']}><QuotationForm /></ProtectedRoute>} />
            <Route path="accounts/invoices" element={<ProtectedRoute requiredPermissions={['invoices.read']}><InvoiceList /></ProtectedRoute>} />
            <Route path="accounts/invoices/batch-print" element={<ProtectedRoute requiredPermissions={['invoices.read']}><BatchInvoicePrint /></ProtectedRoute>} />
            <Route path="accounts/invoices/create" element={<ProtectedRoute requiredPermissions={['invoices.create']}><InvoiceForm /></ProtectedRoute>} />
            <Route path="accounts/invoices/:id" element={<ProtectedRoute requiredPermissions={['invoices.read']}><InvoiceView /></ProtectedRoute>} />
            <Route path="accounts/invoices/:id/edit" element={<ProtectedRoute requiredPermissions={['invoices.update']}><InvoiceForm /></ProtectedRoute>} />
            <Route path="accounts/payments/create" element={<ProtectedRoute requiredPermissions={['payments.create']}><PaymentForm /></ProtectedRoute>} />
            <Route path="accounts/payments/incoming" element={<ProtectedRoute requiredPermissions={['payments.create']}><PaymentForm /></ProtectedRoute>} />
            <Route path="accounts/payments/:id/edit" element={<ProtectedRoute requiredPermissions={['payments.update']}><PaymentForm /></ProtectedRoute>} />
            <Route path="settings" element={<AdminRoute><Settings /></AdminRoute>} />
            <Route path="profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="job-cards" element={<ProtectedRoute requiredPermissions={['jobcards.read']}><JobCards /></ProtectedRoute>} />
            <Route path="job-cards/:id" element={<ProtectedRoute requiredPermissions={['jobcards.read']}><ViewJobCardPage /></ProtectedRoute>} />
            <Route path="all-job-cards" element={<ProtectedRoute requiredPermissions={['jobcards.read']}><AllJobCards /></ProtectedRoute>} />
            <Route path="schedule" element={<ProtectedRoute requiredPermissions={['schedule.read']}><Schedule /></ProtectedRoute>} />
            <Route path="knowledge-base" element={<ProtectedRoute><KnowledgeBase /></ProtectedRoute>} />
            <Route path="customers" element={<ProtectedRoute requiredPermissions={['customers.read']}><CustomerManagement /></ProtectedRoute>} />
            <Route path="support" element={<ProtectedRoute><KnowledgeBase /></ProtectedRoute>} />

            <Route path="reports" element={<ProtectedRoute requiredPermissions={['reports.read']}><Reports /></ProtectedRoute>} />
            <Route path="sale-return" element={<ProtectedRoute requiredPermissions={['sale_return.read']}><SaleReturn /></ProtectedRoute>} />
            <Route path="service-reminder" element={<ProtectedRoute requiredPermissions={['service_reminder.read']}><ServiceReminder /></ProtectedRoute>} />
            <Route path="vendor-payable" element={<ProtectedRoute requiredPermissions={['vendors.read']}><VendorPayable /></ProtectedRoute>} />
          <Route path="branches" element={<ProtectedRoute requiredPermissions={['branches.read']}><Branches /></ProtectedRoute>} />
          <Route path="expense-management" element={<ProtectedRoute requiredPermissions={['expense_management.read']}><ExpenseManagement /></ProtectedRoute>} />
          <Route path="owner-transactions" element={<ProtectedRoute requiredPermissions={['owner_transactions.read']}><OwnerTransactions /></ProtectedRoute>} />
          <Route path="crm" element={<ProtectedRoute requiredPermissions={['crm.read']}><CrmDashboard /></ProtectedRoute>} />
          <Route path="crm/leads" element={<ProtectedRoute requiredPermissions={['crm.read']}><LeadsList /></ProtectedRoute>} />
          <Route path="crm/leads/new" element={<ProtectedRoute requiredPermissions={['crm.create']}><LeadForm /></ProtectedRoute>} />
          <Route path="crm/leads/:id" element={<ProtectedRoute requiredPermissions={['crm.read']}><LeadDetails /></ProtectedRoute>} />
          <Route path="crm/leads/:id/edit" element={<ProtectedRoute requiredPermissions={['crm.update']}><LeadForm /></ProtectedRoute>} />
          <Route path="crm/activities" element={<ProtectedRoute requiredPermissions={['crm.read']}><ActivitiesList /></ProtectedRoute>} />
          <Route path="crm/deals" element={<ProtectedRoute requiredPermissions={['crm.read']}><DealsPipeline /></ProtectedRoute>} />
          <Route path="crm/deals/new" element={<ProtectedRoute requiredPermissions={['crm.create']}><DealForm /></ProtectedRoute>} />
          <Route path="crm/deals/:id" element={<ProtectedRoute requiredPermissions={['crm.read']}><DealDetails /></ProtectedRoute>} />
          <Route path="crm/deals/:id/edit" element={<ProtectedRoute requiredPermissions={['crm.update']}><DealForm /></ProtectedRoute>} />
          <Route path="crm/tasks" element={<ProtectedRoute requiredPermissions={['crm.read']}><TaskManager /></ProtectedRoute>} />
          <Route path="crm/tasks/new" element={<ProtectedRoute requiredPermissions={['crm.create']}><TaskForm /></ProtectedRoute>} />
          <Route path="crm/tasks/:id/edit" element={<ProtectedRoute requiredPermissions={['crm.update']}><TaskForm /></ProtectedRoute>} />
          <Route path="crm/analytics" element={<ProtectedRoute requiredPermissions={['crm.read']}><Analytics /></ProtectedRoute>} />
        </Route>
        )}
      </Routes>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <PermissionsProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AppContent />
          </Router>
        </PermissionsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;